import '../../styles/restScreens.css';
import { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { toneTextStyle } from './clientsConstants';
import {
    buildImportReportCsv, buildImportRows, downloadCsv, guessMapping, parseCsv,
} from '../../lib/contactCsv';
import { METHOD_LABELS } from '../../lib/contactMethods';

import { BTN } from '../ui/platformStandard';
// BL-177 PR C1-C6: import company contacts from a CSV (the imported provider's contact export, or any CSV with a column mapping).
// ALWAYS a dry run first - nothing is written until the admin clicks Import. Never overwrites: empty fields are filled and extra
// phones/emails are ADDED as labeled entries; conflicts are listed. The whole batch can be undone. Admins only (the database refuses anyone else).
// File.text() is missing from older browsers (and test DOMs); FileReader works everywhere.
const readFileText = (file) => (typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = reject; reader.readAsText(file); }));

const field = 'rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)] outline-none focus:border-brand-500';

const COUNT_LABELS = [
    ['rows_read', 'Rows read'], ['new_people', 'New people'], ['new_accounts', 'New accounts'], ['matched', 'Matched to existing'],
    ['extra_phones_added', 'Extra phones added'], ['extra_emails_added', 'Extra emails added'], ['new_account_links', 'Added to another account'],
    ['conflict_count', 'Conflicts (kept as is)'], ['invalid_count', 'Invalid numbers / emails'], ['opted_out', 'Opt-outs carried'], ['skipped', 'Skipped'],
];

function ColumnSelect({ label, headers, value, onChange }) {
    return (
        <label className="flex flex-col gap-1 text-xs text-[var(--ink-muted)]">
            {label}
            <select className={field} value={value} onChange={event => onChange(event.target.value)} aria-label={label}>
                <option value="">— not in this file —</option>
                {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
        </label>
    );
}

function MultiColumn({ title, kind, headers, items, onChange }) {
    const add = () => onChange([...items, { column: '', label: kind === 'email' ? 'work' : 'mobile' }]);
    return (
        <div className="sm:col-span-2">
            <div className="mb-1 text-xs text-[var(--ink-muted)]">{title}</div>
            {items.map((item, index) => (
                <div key={index} className="mb-1.5 flex flex-wrap items-center gap-2">
                    <select className={`${field} min-w-[10rem]`} aria-label={`${title} column ${index + 1}`} value={item.column}
                        onChange={event => onChange(items.map((it, i) => (i === index ? { ...it, column: event.target.value } : it)))}>
                        <option value="">Choose a column…</option>
                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <span className="text-xs text-[var(--ink-muted)]">label</span>
                    <select className={field} aria-label={`${title} label ${index + 1}`} value={item.label}
                        onChange={event => onChange(items.map((it, i) => (i === index ? { ...it, label: event.target.value } : it)))}>
                        {METHOD_LABELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                    <button type="button" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]" aria-label={`Remove ${title} column ${index + 1}`}
                        onClick={() => onChange(items.filter((_, i) => i !== index))}>×</button>
                </div>
            ))}
            <button type="button" className={BTN} aria-label={`Add ${kind} column`} onClick={add}>+</button>
        </div>
    );
}

export default function ContactImportPanel({ entities, onClose, onDone }) {
    const [entityId, setEntityId] = useState(entities.length === 1 ? entities[0].id : '');
    const [filename, setFilename] = useState('');
    const [parsed, setParsed] = useState(null);
    const [mapping, setMapping] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [recent, setRecent] = useState([]);
    const [undoResult, setUndoResult] = useState(null);

    const loadRecent = useCallback(async () => {
        const { data } = await supabaseClient.from('contact_import_batches')
            .select('id, filename, status, counts, created_at').order('created_at', { ascending: false }).limit(5);
        setRecent(data || []);
    }, []);
    useEffect(() => { loadRecent(); }, [loadRecent]);

    const onFile = async (event) => {
        const file = event.target.files?.[0];
        setPreview(null); setResult(null); setError(null); setUndoResult(null);
        if (!file) return;
        const text = await readFileText(file);
        const csv = parseCsv(text);
        setFilename(file.name);
        setParsed(csv);
        setMapping(guessMapping(csv.headers));
    };

    const rows = parsed && mapping ? buildImportRows(parsed, mapping) : [];
    const run = async (apply) => {
        setBusy(true); setError(null);
        try {
            const { data, error: rpcError } = await supabaseClient.rpc('crm_import_contacts_run', {
                p_entity: entityId, p_rows: rows, p_apply: apply, p_filename: filename || null,
            });
            if (rpcError) throw rpcError;
            if (apply) { setResult(data); setPreview(null); await loadRecent(); onDone?.(); } else setPreview(data);
        } catch (err) {
            setError(err.message || String(err));
        } finally { setBusy(false); }
    };
    const undo = async (batchId) => {
        setBusy(true); setError(null);
        try {
            const { data, error: rpcError } = await supabaseClient.rpc('crm_import_contacts_undo', { p_batch: batchId });
            if (rpcError) throw rpcError;
            setUndoResult(data); setResult(null); await loadRecent(); onDone?.();
        } catch (err) { setError(err.message || String(err)); } finally { setBusy(false); }
    };

    const set = (key, value) => setMapping(m => ({ ...m, [key]: value }));
    const shown = result || preview;
    const ready = entityId && parsed && rows.length > 0 && mapping && (mapping.name || mapping.first_name || mapping.last_name || mapping.phones.length || mapping.emails.length);

    return (
        <div data-m6-layout="clients" data-m6-component="ContactImportPanel" className="mb-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4 shadow-sm" data-testid="import-panel">
            <div className="mb-1 flex items-start justify-between">
                <div className="text-sm font-semibold text-[var(--ink)]">Import company contacts from a CSV</div>
                <button type="button" onClick={onClose} className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">Close</button>
            </div>
            <p className="mb-3 text-xs text-[var(--ink-muted)]">
                Upload a contacts CSV. You see a preview first and nothing is saved until you click Import.
                Existing people are never overwritten: empty fields are filled and extra phones and emails are added.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
                {entities.length > 1 && (
                    <label className="flex flex-col gap-1 text-xs text-[var(--ink-muted)]">Company
                        <select className={field} value={entityId} aria-label="Company" onChange={event => { setEntityId(event.target.value); setPreview(null); }}>
                            <option value="">Choose…</option>
                            {entities.map(e => <option key={e.id} value={e.id}>{e.entity_code} — {e.name}</option>)}
                        </select>
                    </label>
                )}
                <label className="flex flex-col gap-1 text-xs text-[var(--ink-muted)]">CSV file
                    <input type="file" accept=".csv,text/csv" onChange={onFile} aria-label="CSV file" className="text-sm text-[var(--ink)]" />
                </label>
            </div>

            {parsed && mapping && (
                <>
                    <div className="mt-3 text-xs text-[var(--ink-muted)]">
                        {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} in {filename}. Check how the columns map:
                    </div>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <ColumnSelect label="Full name" headers={parsed.headers} value={mapping.name} onChange={v => set('name', v)} />
                        <ColumnSelect label="First name" headers={parsed.headers} value={mapping.first_name} onChange={v => set('first_name', v)} />
                        <ColumnSelect label="Last name" headers={parsed.headers} value={mapping.last_name} onChange={v => set('last_name', v)} />
                        <ColumnSelect label="Company / account name" headers={parsed.headers} value={mapping.account_name} onChange={v => set('account_name', v)} />
                        <ColumnSelect label="Account type (Business / Personal)" headers={parsed.headers} value={mapping.account_kind} onChange={v => set('account_kind', v)} />
                        <ColumnSelect label="Title" headers={parsed.headers} value={mapping.title} onChange={v => set('title', v)} />
                        <ColumnSelect label="Tags" headers={parsed.headers} value={mapping.tags} onChange={v => set('tags', v)} />
                        <ColumnSelect label="Opted out / unsubscribed" headers={parsed.headers} value={mapping.opted_out} onChange={v => set('opted_out', v)} />
                        <MultiColumn title="Phone columns" kind="phone" headers={parsed.headers} items={mapping.phones} onChange={v => set('phones', v)} />
                        <MultiColumn title="Email columns" kind="email" headers={parsed.headers} items={mapping.emails} onChange={v => set('emails', v)} />
                        <MultiColumn title="Address columns" kind="address" headers={parsed.headers} items={mapping.addresses} onChange={v => set('addresses', v)} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" disabled={!ready || busy} onClick={() => run(false)}
                            className={BTN}>
                            {busy && !preview ? 'Checking…' : 'Preview import'}
                        </button>
                        {preview && (
                            <button type="button" disabled={busy} onClick={() => run(true)}
                                className={BTN}>
                                Import {preview.new_people} new, update {preview.matched} existing
                            </button>
                        )}
                    </div>
                </>
            )}

            {error && <div className="mt-3 rounded border bg-[var(--ui-alert)]/10 p-2 text-sm" style={toneTextStyle('alert')}>{error}</div>}

            {shown && (
                <div className="mt-4" data-testid="import-report">
                    <div className="mb-2 text-sm font-semibold text-[var(--ink)]">{result ? 'Imported' : 'Preview — nothing has been saved yet'}</div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {COUNT_LABELS.map(([key, label]) => (
                            <div key={key} className="rounded border border-[var(--surface-border)] px-2 py-1.5">
                                <div className="text-lg font-semibold text-[var(--ink)]">{shown[key] ?? 0}</div>
                                <div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">{label}</div>
                            </div>
                        ))}
                    </div>
                    {(shown.conflicts || []).length > 0 && (
                        <ul className="mt-3 list-disc pl-5 text-xs text-[var(--ink-muted)]">
                            {shown.conflicts.slice(0, 20).map((c, i) => <li key={i}>Row {c.row}: {c.field} — file says “{c.file}”, kept “{c.existing}”</li>)}
                        </ul>
                    )}
                    {(shown.invalid || []).length > 0 && (
                        <ul className="mt-2 list-disc pl-5 text-xs text-[var(--ink-muted)]">
                            {shown.invalid.slice(0, 20).map((c, i) => <li key={i}>Row {c.row}: {c.kind} “{c.value}” is not valid and was left out</li>)}
                        </ul>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" className={BTN}
                            onClick={() => downloadCsv(`contact-import-${result ? 'report' : 'preview'}.csv`, buildImportReportCsv(shown))}>Download report (CSV)</button>
                        {result?.batch_id && (
                            <button type="button" disabled={busy} className={BTN} onClick={() => undo(result.batch_id)}>
                                Undo this import
                            </button>
                        )}
                    </div>
                </div>
            )}

            {undoResult && (
                <div className="mt-3 text-sm text-[var(--ink)]">
                    Undone: {undoResult.people_removed} people and {undoResult.accounts_removed} accounts removed
                    {(undoResult.kept_because_in_use || []).length > 0 && ` (${undoResult.kept_because_in_use.length} kept because they are already in use)`}.
                </div>
            )}

            {recent.length > 0 && (
                <div className="mt-4 border-t border-[var(--surface-border)] pt-3">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Recent imports</div>
                    {recent.map(batch => (
                        <div key={batch.id} className="flex flex-wrap items-center gap-3 py-0.5 text-xs text-[var(--ink-muted)]">
                            <span>{new Date(batch.created_at).toLocaleString()}</span>
                            <span>{batch.filename || 'CSV'}</span>
                            <span>{batch.counts?.new_people ?? 0} new · {batch.counts?.matched ?? 0} matched</span>
                            <span>{batch.status}</span>
                            {batch.status === 'applied' && (
                                <button type="button" disabled={busy} className="underline" onClick={() => undo(batch.id)}>Undo</button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
